import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useV2Auth } from '../V2App'
import { useV2Api } from '../../../hooks/useV2Api'
import { friendlyErrorMessage } from '../../../utils/friendlyError'
import SmartAddressInput from './SmartAddressInput'
import './NewQuotePage.css'

const INCOTERMS = [
  { value: 'EXW', label: 'EXW — Ex Works' },
  { value: 'FCA', label: 'FCA — Free Carrier' },
  { value: 'FOB', label: 'FOB — Free On Board' },
  { value: 'CFR', label: 'CFR — Cost and Freight' },
  { value: 'CIF', label: 'CIF — Cost, Insurance, Freight' },
  { value: 'CIP', label: 'CIP — Carriage and Insurance Paid' },
  { value: 'CPT', label: 'CPT — Carriage Paid To' },
  { value: 'DAP', label: 'DAP — Delivered at Place' },
  { value: 'DDP', label: 'DDP — Delivered Duty Paid' }
]

const FCL_CONTAINERS = [
  { code: '20GP', label: '20 foot standard' },
  { code: '40GP', label: '40 foot standard' },
  { code: '40HC', label: '40 foot high cube' },
  { code: '45HC', label: '45 foot high cube' }
]

const TRUCK_TYPES = [
  { value: 'dry_van', label: 'Dry van' },
  { value: 'reefer', label: 'Reefer' },
  { value: 'flatbed', label: 'Flatbed' },
  { value: 'step_deck', label: 'Step deck' },
  { value: 'hotshot', label: 'Hotshot' }
]

// Truckload mode decides WHICH CARRIERS receive the RFQ (v2_067): a carrier
// marked FTL-only never sees an LTL load. Left blank it stays unset, and an
// unset mode goes to every trucking carrier — which is why "not sure" is a
// real option here and not a validation error.
const SHIPMENT_MODES = [
  { value: '', label: 'Not sure yet' },
  { value: 'FTL', label: 'FTL — full truckload' },
  { value: 'LTL', label: 'LTL — less than truckload' }
]

function IncotermPicker({ value, onChange }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="nq-incoterm" onMouseLeave={() => setOpen(false)}>
      <button type="button" className="nq-field-button" onClick={() => setOpen(o => !o)}>
        <span className="nq-field-label">Incoterm</span>
        <span className="nq-field-value">{value}</span>
      </button>
      {open && (
        <ul className="nq-dropdown">
          {INCOTERMS.map(o => (
            <li key={o.value}
                className={`nq-dropdown-item${o.value === value ? ' is-active' : ''}`}
                onClick={() => { onChange(o.value); setOpen(false) }}>
              <strong>{o.value}</strong> <span style={{ opacity: .7 }}>{o.label.replace(o.value + ' — ', '')}</span>
            </li>
          ))}
          <li className="nq-dropdown-foot">
            Incoterms® define which party pays for and handles each leg of the move.
          </li>
        </ul>
      )}
    </div>
  )
}

function CargoPanel({ value, onChange }) {
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState(value.cargoMode === 'trucking' || value.cargoMode === 'air' || value.cargoMode === 'ocean_lcl' ? 'other' : 'fcl')

  const containers = value.cargoConfig?.containers || {}
  const totalUnits = Object.values(containers).reduce((acc, n) => acc + (Number(n) || 0), 0)

  const summary = (() => {
    if (value.cargoMode === 'ocean_fcl') {
      const parts = Object.entries(containers).filter(([, n]) => n > 0).map(([k, n]) => `${n}× ${k}`)
      return parts.length ? parts.join(', ') + (value.dangerousGoods ? ', DG' : '') : 'Provide cargo details'
    }
    if (value.cargoMode === 'ocean_lcl') {
      const w = value.cargoConfig?.totalWeight
      const p = value.cargoConfig?.pallets
      return p ? `LCL — ${p} pallets${w ? `, ${w} kg` : ''}` : (w ? `LCL — ${w} kg` : 'LCL — provide details')
    }
    if (value.cargoMode === 'air')      return value.cargoConfig?.totalWeight ? `Air — ${value.cargoConfig.totalWeight} kg` : 'Air — provide weight'
    if (value.cargoMode === 'trucking') {
      const truck = TRUCK_TYPES.find(t => t.value === value.cargoConfig?.truckType)?.label || 'Truck'
      const mode = value.cargoConfig?.shipmentMode ? `${value.cargoConfig.shipmentMode} ` : ''
      return value.cargoConfig?.totalWeight
        ? `${mode}${truck} — ${value.cargoConfig.totalWeight} lb`
        : `${mode}${truck} — provide weight`
    }
    return 'Provide cargo details'
  })()

  const setContainer = (code, n) => onChange({
    ...value,
    cargoMode: 'ocean_fcl',
    cargoConfig: { ...value.cargoConfig, containers: { ...containers, [code]: Math.max(0, n) } }
  })

  const setOther = (mode, patch) => onChange({
    ...value,
    cargoMode: mode,
    cargoConfig: { ...(value.cargoConfig || {}), ...patch }
  })

  return (
    <div className="nq-cargo">
      <button type="button" className="nq-field-button" onClick={() => setOpen(o => !o)}>
        <span className="nq-field-label">Cargo</span>
        <span className="nq-field-value">{summary}</span>
      </button>
      {open && (
        <div className="nq-cargo-panel">
          <div className="nq-cargo-tabs">
            <button type="button"
                    className={`nq-cargo-tab${tab === 'fcl' ? ' is-active' : ''}`}
                    onClick={() => { setTab('fcl'); onChange({ ...value, cargoMode: 'ocean_fcl' }) }}>
              Ocean FCL
            </button>
            <button type="button"
                    className={`nq-cargo-tab${tab === 'other' ? ' is-active' : ''}`}
                    onClick={() => setTab('other')}>
              LCL, Air & Trucking Only
            </button>
          </div>

          {tab === 'fcl' ? (
            <>
              <div className="nq-cargo-section-title">Container Type</div>
              {FCL_CONTAINERS.map(c => (
                <div className="nq-cargo-row" key={c.code}>
                  <span className="nq-cargo-row-label">{c.label}</span>
                  <div className="nq-stepper">
                    <button type="button" onClick={() => setContainer(c.code, (containers[c.code] || 0) - 1)} disabled={(containers[c.code] || 0) === 0}>−</button>
                    <span className="nq-stepper-value">{containers[c.code] || 0}</span>
                    <button type="button" onClick={() => setContainer(c.code, (containers[c.code] || 0) + 1)}>+</button>
                  </div>
                </div>
              ))}
              <div className="nq-cargo-section-title nq-cargo-section-title--spaced">Dangerous Goods</div>
              <div className="nq-cargo-help">
                Does your cargo contain any dangerous goods including batteries, hazardous materials, magnets, creams, liquids, or powders?
              </div>
              <label className="nq-radio">
                <input type="radio" checked={value.dangerousGoods === true} onChange={() => onChange({ ...value, dangerousGoods: true })} />
                Yes, my cargo contains dangerous goods.
              </label>
              <label className="nq-radio">
                <input type="radio" checked={value.dangerousGoods !== true} onChange={() => onChange({ ...value, dangerousGoods: false })} />
                No, my cargo does not contain dangerous goods.
              </label>
            </>
          ) : (
            <>
              <div className="nq-cargo-section-title">What are you shipping?</div>
              <div className="nq-cargo-row-other">
                <label className="nq-radio">
                  <input type="radio"
                         checked={value.cargoMode === 'ocean_lcl'}
                         onChange={() => setOther('ocean_lcl', {})} />
                  Ocean LCL
                </label>
                <label className="nq-radio">
                  <input type="radio"
                         checked={value.cargoMode === 'air'}
                         onChange={() => setOther('air', {})} />
                  Air
                </label>
                <label className="nq-radio">
                  <input type="radio"
                         checked={value.cargoMode === 'trucking'}
                         onChange={() => setOther('trucking', { truckType: value.cargoConfig?.truckType || 'dry_van' })} />
                  Trucking (US domestic)
                </label>
              </div>
              {(value.cargoMode === 'ocean_lcl' || value.cargoMode === 'air' || value.cargoMode === 'trucking') && (
                <>
                  {value.cargoMode === 'ocean_lcl' && (
                    <label className="nq-cargo-input">
                      <span className="nq-cargo-input-label">Pallet count</span>
                      <input type="number" min="1"
                             value={value.cargoConfig?.pallets || ''}
                             onChange={e => setOther('ocean_lcl', { pallets: Number(e.target.value) || null })} />
                    </label>
                  )}
                  {value.cargoMode === 'trucking' && (
                    <>
                      <label className="nq-cargo-input">
                        <span className="nq-cargo-input-label">Truck type</span>
                        <select
                          value={value.cargoConfig?.truckType || 'dry_van'}
                          onChange={e => setOther('trucking', { truckType: e.target.value })}
                        >
                          {TRUCK_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </label>
                      <label className="nq-cargo-input">
                        <span className="nq-cargo-input-label">Truckload mode</span>
                        <select
                          value={value.cargoConfig?.shipmentMode || ''}
                          onChange={e => setOther('trucking', { shipmentMode: e.target.value || null })}
                        >
                          {SHIPMENT_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                        </select>
                      </label>
                    </>
                  )}
                  <label className="nq-cargo-input">
                    <span className="nq-cargo-input-label">
                      Total weight {value.cargoMode === 'trucking' ? '(lb)' : '(kg)'}
                    </span>
                    <input type="number" min="0" step="0.01"
                           value={value.cargoConfig?.totalWeight || ''}
                           onChange={e => setOther(value.cargoMode, { totalWeight: Number(e.target.value) || null })} />
                  </label>
                  <div className="nq-cargo-section-title nq-cargo-section-title--spaced">Dangerous Goods</div>
                  <label className="nq-radio">
                    <input type="radio" checked={value.dangerousGoods === true} onChange={() => onChange({ ...value, dangerousGoods: true })} />
                    Yes
                  </label>
                  <label className="nq-radio">
                    <input type="radio" checked={value.dangerousGoods !== true} onChange={() => onChange({ ...value, dangerousGoods: false })} />
                    No
                  </label>
                </>
              )}
            </>
          )}

          <div className="nq-cargo-actions">
            <button type="button" className="nq-btn nq-btn--primary" onClick={() => setOpen(false)} disabled={value.cargoMode === 'ocean_fcl' && totalUnits === 0}>
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default function NewQuotePage({ onCreated, onCancel } = {}) {
  useV2Auth()
  const api = useV2Api()
  const navigate = useNavigate()

  const [incoterm, setIncoterm] = useState('FOB')
  const [origin, setOrigin] = useState(null)
  const [destination, setDestination] = useState(null)
  const [cargo, setCargo] = useState({
    cargoMode: 'trucking',
    cargoConfig: { truckType: 'dry_van' },
    dangerousGoods: false
  })
  const [cargoReadyDate, setCargoReadyDate] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  const today = new Date().toISOString().slice(0, 10)

  const handleSubmit = async (e) => {
    e?.preventDefault()
    setError(null)

    if (!origin) return setError('Pickup address is required')
    if (!destination) return setError('Delivery address is required')
    if (!cargoReadyDate) return setError('Cargo ready date is required')
    if (cargo.cargoMode === 'ocean_fcl') {
      const total = Object.values(cargo.cargoConfig.containers || {}).reduce((a, n) => a + (Number(n) || 0), 0)
      if (total === 0) return setError('Select at least one container')
    } else if (!cargo.cargoConfig.totalWeight) {
      return setError('Total weight is required for LCL / air / trucking')
    } else if (cargo.cargoMode === 'trucking' && !cargo.cargoConfig.truckType) {
      return setError('Truck type is required for trucking')
    }

    setSubmitting(true)
    try {
      const res = await api.submitNewQuote({
        incoterm: cargo.cargoMode === 'trucking' ? undefined : incoterm,
        origin,
        destination,
        cargoMode: cargo.cargoMode,
        cargoConfig: cargo.cargoConfig,
        dangerousGoods: cargo.dangerousGoods,
        cargoReadyDate
      })
      const shipmentId = res?.data?.shipmentId
      if (shipmentId) {
        if (onCreated) {
          onCreated(shipmentId)
        } else {
          navigate(`/workspace/${shipmentId}`)
        }
      } else {
        setError('Submitted, but no shipment id was returned. Check the shipments list.')
      }
    } catch (err) {
      setError(err)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="nq-page">
      <div className="nq-header">
        <h1 className="nq-title">Get trucking rates</h1>
        <p className="nq-subtitle">Create a domestic load first, then choose which providers receive the RFQ.</p>
      </div>

      <form className="nq-form" onSubmit={handleSubmit}>
        <div className={`nq-form-group${cargo.cargoMode === 'trucking' ? ' nq-form-group--no-incoterm' : ''}`}>
          {cargo.cargoMode !== 'trucking' && (
            <IncotermPicker value={incoterm} onChange={setIncoterm} />
          )}

          <div className="nq-field nq-field--address">
            <span className="nq-field-label">Pickup Address</span>
            <SmartAddressInput value={origin} onChange={setOrigin} side="origin" />
          </div>
          <div className="nq-arrow">→</div>
          <div className="nq-field nq-field--address">
            <span className="nq-field-label">Delivery Address</span>
            <SmartAddressInput value={destination} onChange={setDestination} side="destination" />
          </div>

          <CargoPanel value={cargo} onChange={setCargo} />

          <div className="nq-field nq-field--date">
            <span className="nq-field-label">Cargo Ready Date</span>
            <input
              type="date"
              min={today}
              value={cargoReadyDate}
              onChange={e => setCargoReadyDate(e.target.value)}
              className="nq-field-input"
            />
          </div>

          <button type="submit" className="nq-submit" disabled={submitting} aria-label="Get rates">
            {submitting ? '…' : '🔍'}
          </button>
        </div>

        {error && <div className="nq-error">{friendlyErrorMessage(error, 'Failed to submit')}</div>}
      </form>
    </div>
  )
}
